import { axisFor, EvalRunDetail, Severity } from '../../api/ibra';

/**
 * A run as Markdown, for pasting somewhere else.
 *
 * Text rather than an image, and the reason is accuracy before economy. The
 * numbers on this screen are the entire point of it, and a screenshot means
 * whoever reads it next is reading counts off pixels. Text arrives exact.
 *
 * It is also far smaller — a rendered modal is on the order of a couple of
 * thousand tokens to an LLM, the same report as Markdown is a few hundred —
 * but that would be the weaker argument on its own.
 */
export function runToMarkdown(d: EvalRunDetail): string {
  const pct = (n: number) => `${(n * 100).toFixed(1)}%`;
  const out: string[] = [];
  const isActions = d.suite === 'actions';
  const { keys, label } = axisFor(d.suite);
  const axisName = isActions ? 'outcome' : 'lane';

  out.push(`# Ibra ${isActions ? 'action' : 'lane'} eval — ${d.model}`);
  out.push('');
  out.push(
    `Run ${new Date(d.startedAt).toISOString()} · **${d.correct}/${d.total} correct** (${pct(d.accuracy)})`,
  );
  out.push('');
  out.push(
    `| critical | major | minor |\n|---|---|---|\n| ${d.critical} | ${d.major} | ${d.minor} |`,
  );

  if (d.status === 'failed') {
    out.push('');
    out.push(`**Run failed:** ${d.error ?? 'no reason recorded'}`);
    return out.join('\n');
  }

  if (d.byLane) {
    out.push('');
    out.push(`## Recall by ${axisName}`);
    out.push('');
    out.push(`| ${isActions ? 'Outcome' : 'Lane'} | Correct | Recall |`);
    out.push('|---|---|---|');
    for (const key of keys) {
      const l = d.byLane[key];
      if (!l) continue;
      out.push(
        `| ${label(key)} | ${l.correct}/${l.expected} | ${pct(l.recall)} |`,
      );
    }
  }

  if (d.confusion) {
    out.push('');
    out.push('## Confusion');
    out.push('');
    out.push(
      isActions
        ? 'Rows are what the message should have got; columns are what it got.'
        : 'Rows are what the question should have been; columns are where it went.',
    );
    out.push('');
    out.push(`| expected \\ actual | ${keys.map(label).join(' | ')} |`);
    out.push(`|---|${keys.map(() => '---').join('|')}|`);
    for (const expected of keys) {
      const cells = keys.map((actual) => d.confusion?.[expected]?.[actual] ?? 0);
      out.push(`| ${label(expected)} | ${cells.join(' | ')} |`);
    }
  }

  const failures = d.failures ?? [];
  out.push('');
  out.push(isActions ? '## Wrong calls' : '## Misroutes');
  if (failures.length === 0) {
    out.push('');
    out.push(
      isActions
        ? 'None — every message got the outcome it should have.'
        : 'None — every question landed in the right lane.',
    );
  } else {
    const order: Severity[] = ['critical', 'major', 'minor'];
    for (const sev of order) {
      const rows = failures.filter((f) => f.severity === sev);
      if (rows.length === 0) continue;
      out.push('');
      out.push(`### ${sev} (${rows.length})`);
      out.push('');
      for (const f of rows) {
        out.push(
          `- "${f.question}" — expected **${label(f.expected)}**, got **${label(f.actual)}**${f.reason ? ` (${f.reason})` : ''}`,
        );
        // Corpus notes travel with the case. Several are deliberately
        // arguable, and a reader deciding whether a failure is real needs the
        // reasoning that put the case there.
        if (f.note) out.push(`  - note: ${f.note}`);
      }
    }
  }

  out.push('');
  return out.join('\n');
}

/** Clipboard, with a fallback for browsers or contexts where the async
 *  clipboard API is unavailable (it needs a secure origin). */
export async function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const el = document.createElement('textarea');
  el.value = text;
  el.style.position = 'fixed';
  el.style.opacity = '0';
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
}

export function downloadText(filename: string, text: string): void {
  const url = URL.createObjectURL(
    new Blob([text], { type: 'text/markdown;charset=utf-8' }),
  );
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function runFilename(d: EvalRunDetail): string {
  const stamp = new Date(d.startedAt).toISOString().slice(0, 16).replace(/[:T]/g, '-');
  const model = d.model.replace(/[^a-z0-9.-]/gi, '_');
  return `ibra-${d.suite}-eval-${model}-${stamp}.md`;
}
